import React, { useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Collapse,
  useTheme,
  Grid,
} from "@mui/material";
import AuctionEdgeForm from "./services/AuctionEdgeForm";
import CRSForm from "./services/CRSForm";
import DealersCircleForm from "./services/DealersCircleForm";
import HomenetForm from "./services/HomenetForm";
import OctaneForm from "./services/OctaneForm";
import SIAForm from "./services/SIAForm";
import VincueForm from "./services/VincueForm";
import XciraForm from "./services/XciraForm";

const SERVICE_COMPONENTS: Record<string, React.FC<any>> = {
  auctionEdge: AuctionEdgeForm,
  crs: CRSForm,
  dealersCircle: DealersCircleForm,
  homenet: HomenetForm,
  octane: OctaneForm,
  sia: SIAForm,
  vincue: VincueForm,
  xcira: XciraForm,
};

interface AssetSyndicationFormProps {
  config: any;
  handleChange: (path: string, value: any) => void;
}

const SERVICE_OPTIONS = Object.keys(SERVICE_COMPONENTS);

const AssetSyndicationForm: React.FC<AssetSyndicationFormProps> = ({
  config,
  handleChange,
}) => {
  const theme = useTheme();
  const red = "#C62828"; // UMS red
  const cachedConfigsRef = useRef<Record<string, any>>({});

  const enabledServices = useMemo(() => {
    return SERVICE_OPTIONS.filter(
      (s) => config[`assetSyndication_${s}_serviceConfig`]
    );
  }, [config]);

  const toggleService = (service: string, enabled: boolean) => {
    const serviceKey = `assetSyndication_${service}_serviceConfig`;
    const stepFlag = `assetSyndication_${service}_serviceFlag`;

    let flowSteps: string[] = [];
    const fc = config.flowControl_assetSyndication_steps;
    if (Array.isArray(fc)) flowSteps = [...fc];
    else if (typeof fc === "object" && fc !== null)
      flowSteps = Object.values(fc).map(String);

    if (enabled) {
      if (!flowSteps.includes(stepFlag)) flowSteps.push(stepFlag);
      const restored = cachedConfigsRef.current[service] || {};
      handleChange(serviceKey, restored);
      cachedConfigsRef.current[service] = restored;
    } else {
      cachedConfigsRef.current[service] = config[serviceKey];
      flowSteps = flowSteps.filter((s) => s !== stepFlag);
      handleChange(serviceKey, undefined);
    }

    handleChange("flowControl_assetSyndication_steps", flowSteps);
  };

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: "0 0 6px rgba(0,0,0,0.3)",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: 600,
          color: red,
          letterSpacing: 0.3,
        }}
      >
        Asset Syndication Services
      </Typography>

      {/* ---------- TOP ROW OF CHECKBOXES ---------- */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {SERVICE_OPTIONS.map((service) => {
          const enabled = enabledServices.includes(service);
          return (
            <Grid item xs="auto" key={service}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabled}
                    onChange={(e) => toggleService(service, e.target.checked)}
                    sx={{
                      color: red,
                      "&.Mui-checked": { color: red },
                      "&:hover": { backgroundColor: "rgba(198,40,40,0.1)" },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {service.charAt(0).toUpperCase() + service.slice(1)}
                  </Typography>
                }
              />
            </Grid>
          );
        })}
      </Grid>

      {/* ---------- STACKED CHILD COMPONENTS ---------- */}
      {SERVICE_OPTIONS.map((service) => {
        const Component = SERVICE_COMPONENTS[service];
        const serviceKey = `assetSyndication_${service}_serviceConfig`;
        const enabled = enabledServices.includes(service);

        return (
          <Collapse in={enabled} unmountOnExit key={service}>
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderLeft: `3px solid ${red}`,
                borderRadius: 1,
                backgroundColor: "rgba(198,40,40,0.05)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: red,
                }}
              >
                {service.charAt(0).toUpperCase() + service.slice(1)} Settings
              </Typography>

              <Component
                config={config[serviceKey] || {}}
                onChange={(field: string, value: any) =>
                  handleChange(`${serviceKey}.${field}`, value)
                }
              />
            </Box>
          </Collapse>
        );
      })}
    </Paper>
  );
};

export default AssetSyndicationForm;
